/**
 * EditTitleModal - Modal de edição do título do ticket.
 *
 * DS 1.9 — a edição do título deixa de ser inline no header e passa a um modal
 * dedicado (botão lápis → modal, nunca inline). Preserva 1:1 a lógica de save
 * do editor inline legado do `TicketDetailHeader`: `trim()` + só grava quando o
 * valor realmente muda (senão apenas fecha).
 *
 * Modal custom (não o `Modal` shared) — mesmo padrão hand-rolled já usado neste
 * módulo (`StageChangeConfirmation`, `ResolveTicketModal`): tema claro fixo via
 * hex hardcoded + `LIGHT_THEME_OVERRIDE` (o `Input` shared lê as CSS vars
 * `--theme-input-*`, que sem o override herdariam o tema dark da página).
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

const LIGHT_THEME_OVERRIDE: React.CSSProperties = {
  '--theme-text': '#1e293b',
  '--theme-muted-text': '#6b7280',
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-card-background': '#ffffff',
  '--theme-card-text': '#334155',
  '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-primary': '#8b5cf6',
} as React.CSSProperties;

export interface EditTitleModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTitle: string;
  onSave: (title: string) => void;
  loading?: boolean;
}

export const EditTitleModal: React.FC<EditTitleModalProps> = ({
  isOpen,
  onClose,
  currentTitle,
  onSave,
  loading = false,
}) => {
  const [value, setValue] = useState(currentTitle);

  useEffect(() => {
    if (isOpen) setValue(currentTitle);
  }, [isOpen, currentTitle]);

  if (!isOpen) return null;

  const handleSave = () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== currentTitle) {
      onSave(trimmed);
    }
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-[60] p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      /* form: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
      data-testid="ticket-edit-title-modal"
    >
      <div
        className="rounded-lg shadow-xl max-w-md w-full"
        style={{ backgroundColor: '#ffffff', ...LIGHT_THEME_OVERRIDE }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#f3e8ff' }}>
              <Icon name="edit" className="w-5 h-5" style={{ color: '#8b5cf6' }} />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
              Editar título
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            disabled={loading}
            data-testid="ticket-edit-title-modal-close"
            aria-label="Fechar"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <label
            htmlFor="ticket-edit-title-input"
            className="block text-xs font-medium mb-1.5"
            style={{ color: '#475569' }}
          >
            Título do ticket
          </label>
          <Input
            id="ticket-edit-title-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            disabled={loading}
            data-testid="ticket-edit-title-input"
          />
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t" style={{ borderColor: '#e5e7eb' }}>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border disabled:opacity-50 transition-colors"
            style={{ color: '#1e293b', backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
            disabled={loading}
            data-testid="ticket-edit-title-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
            style={{ backgroundColor: '#8b5cf6' }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#7c3aed'; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
            disabled={loading || !value.trim()}
            data-testid="ticket-edit-title-save"
          >
            <Icon name="check" className="w-4 h-4" />
            Salvar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

EditTitleModal.displayName = 'EditTitleModal';
